import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  AtmosphericParticles,
  DynamicSubtitle,
  SpecularShine,
} from "./components";

export const KyuStoreNotifPromo: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Design Tokens: Clean Esport Light Theme
  const bgCanvas = "#F8F6FE";
  const bgCard = "#FFFFFF";
  const kyuPurple = "#5D3FD3";
  const purpleLight = "#F3E8FF";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldSuccess = "#10B981";
  const emeraldLight = "#ECFDF5";
  const solarGold = "#FFB020";
  const goldLight = "#FEF3C7";

  const EASE = Easing.bezier(0.16, 1, 0.3, 1);

  const sp = (delay: number, damping = 15, stiffness = 120) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // =========================================================================
  // SUBTITLE SYNCHRONIZATION (Pinned at safe band bottom: 400px)
  // =========================================================================
  const SUBTITLES = [
    { from: 0, to: 116, text: "Gak perlu refresh web berkali-kali cuma buat ngecek diamond masuk!", highlight: "Gak perlu refresh web" },
    { from: 116, to: 225, text: "Di Kyu Store, ada fitur notifikasi otomatis.", highlight: "notifikasi otomatis" },
    { from: 225, to: 425, text: "Selesai bayar QRIS, status pesananmu langsung di-update live detik per detik sampai diamond masuk aman.", highlight: "di-update live detik per detik" },
    { from: 425, to: 580, text: "Gak cuma itu! Kamu juga gak bakal ketinggalan promo flash sale dan event skin murah...", highlight: "promo flash sale dan event skin" },
    { from: 580, to: 675, text: "...karena radarnya langsung ngabarin ke HP kamu duluan!", highlight: "ngabarin ke HP kamu duluan!" },
    { from: 675, to: 745, text: "Top up tenang, promo selalu dapat.", highlight: "Top up tenang" },
    { from: 745, to: 900, text: "Aktifkan notifikasimu sekarang di kyustore.com!", highlight: "kyustore.com!" },
  ];

  // =========================================================================
  // TIMELINE BEATS:
  // Beat 1: The Frustration Hook (0 - 110)
  // Beat 2: Realtime Order Status Tracking (110 - 425)
  // Beat 3: Flash Sale & Promo Radar (425 - 675)
  // Beat 4: Resolution & CTA (675 - 900)
  // =========================================================================

  // Camera 3D Dynamics across video
  const scene1Intro = sp(0, 16, 110);
  const camRotY = interpolate(frame, [0, 110, 425, 675, 900], [0, -6, 4, -4, 0], {
    easing: EASE,
  });
  const camRotX = interpolate(frame, [0, 110, 425, 675, 900], [0, 5, 2, 4, 0], {
    easing: EASE,
  });

  // Beat 1: Tap Refresh Animation & Pop-up Notification
  const tapCycle = Math.sin(frame * 0.4) > 0.3 ? 0.94 : 1.0;
  const notif1Pop = sp(34, 13, 140); // Notification pill pops in at frame 34
  const notif1Scale = interpolate(notif1Pop, [0, 1], [0.6, 1]);
  const notif1Y = interpolate(notif1Pop, [0, 1], [40, 0]);

  // Beat 2: Live Tracking Steps (110 - 425)
  const b2Step1 = sp(140, 14, 130);
  const b2Step2 = sp(190, 14, 130);
  const b2Diamond = sp(240, 12, 140);

  // Beat 3: Bell ring shockwave & Flash Sale Card (425 - 675)
  const bellRing = Math.sin((frame - 430) * 0.6) * Math.exp(-((frame - 430) % 60) * 0.05) * 14;
  const b3CardSpring = sp(440, 14, 120);
  const discountBounce = Math.sin(frame * 0.2) * 3;

  // Beat 4: Final CTA Spring (675 - 900)
  const b4Spring = sp(680, 14, 115);
  const ctaPulse = Math.sin((frame - 740) * 0.15) * 0.03 + 1.0;

  // Specular shine sweeps periodically
  const shineProgress = interpolate(frame % 220, [0, 55], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: EASE,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* 1. Master Audio & SFX Cues */}
      <Audio src={staticFile("kyustore_notif_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />

      {/* SFX Synchronized Beats */}
      {frame === 8 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 20 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 35 && <Audio src={staticFile("ui-pop.mp3")} volume={0.8} />}
      {frame === 36 && <Audio src={staticFile("chime.mp3")} volume={0.7} />}

      {frame === 115 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.6} />}
      {frame === 145 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 195 && <Audio src={staticFile("ui-success.mp3")} volume={0.75} />}
      {frame === 245 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}

      {frame === 430 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.65} />}
      {frame === 440 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

      {frame === 680 && <Audio src={staticFile("whoosh.mp3")} volume={0.7} />}
      {frame === 750 && <Audio src={staticFile("click.mp3")} volume={0.7} />}

      {/* Multi-Depth Subtle Grid Pattern */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Center Ambient Lighting */}
      <div
        style={{
          position: "absolute",
          top: "45%",
          left: "50%",
          width: 900,
          height: 900,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.12) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Header HUD Bar */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Img src={staticFile("kyu_logo.png")} style={{ height: 48, objectFit: "contain" }} />
          <div style={{ width: 2, height: 26, backgroundColor: "#CBD5E1" }} />
          <span
            style={{
              fontFamily: "'Chakra Petch', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: textMuted,
            }}
          >
            [ OFFICIAL STORE FEATURES ]
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: bgCard,
            border: `2px solid ${emeraldSuccess}`,
            padding: "8px 22px",
            borderRadius: 24,
            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: emeraldSuccess, boxShadow: "0 0 10px #10B981" }} />
          <span
            style={{
              fontFamily: "'Chakra Petch', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              color: emeraldSuccess,
              letterSpacing: "0.08em",
            }}
          >
            LIVE NOTIFICATION SYSTEM
          </span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3D PERSPECTIVE VIEWPORT (1400px)                                          */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${camRotY}deg) rotateX(${camRotX}deg)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >

          {/* ===================================================================== */}
          {/* SCENE 1: THE FRUSTRATION HOOK (Frames 0 - 110)                        */}
          {/* ===================================================================== */}
          {frame < 120 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [95, 115], [1, 0], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              {/* Giant Problem Hook Banner */}
              <div
                style={{
                  backgroundColor: "#FEE2E2",
                  border: "2px solid #EF4444",
                  borderRadius: 20,
                  padding: "8px 24px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 18,
                  boxShadow: "0 6px 20px rgba(239, 68, 68, 0.15)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="3">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: "#B91C1C", letterSpacing: "0.06em" }}>
                  SERING CEMAS NUNGGUIN DIAMOND MASUK?
                </span>
              </div>

              <h1
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 70,
                  textAlign: "center",
                  lineHeight: 1.02,
                  marginBottom: 24,
                  textShadow: "0 8px 25px rgba(0,0,0,0.06)",
                }}
              >
                STOP REFRESH WEB PANIK! <br />
                <span style={{ color: kyuPurple }}>STATUS PESANAN DI-UPDATE OTOMATIS</span>
              </h1>

              {/* Simulated Mobile Browser Mockup with Reload Animation */}
              <div
                style={{
                  width: 640,
                  height: 480,
                  backgroundColor: bgCard,
                  borderRadius: 34,
                  border: "2px solid #E2E8F0",
                  boxShadow: "0 30px 80px rgba(93, 63, 211, 0.15), 0 10px 25px rgba(0,0,0,0.04)",
                  padding: 24,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Browser Address Bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#F1F5F9",
                    padding: "12px 22px",
                    borderRadius: 20,
                    marginBottom: 24,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 20, fontWeight: 700, color: textDark }}>
                      https://kyustore.com/order-status
                    </span>
                  </div>

                  {/* Spinning Reload Button (Tapping frantically) */}
                  <div
                    style={{
                      transform: `scale(${tapCycle}) rotate(${frame * 12}deg)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: kyuPurple,
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                    </svg>
                  </div>
                </div>

                {/* Inner Content Placeholder */}
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 22, fontWeight: 700, color: textMuted }}>
                    Nomor Pesanan: #KYU-8829104
                  </div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 44, color: textDark, marginTop: 4 }}>
                    WEEKLY DIAMOND PASS MLBB
                  </div>
                </div>

                {/* ================================================================= */}
                {/* SNAP NOTIFICATION POP-UP (Triggers at frame 34 with sound)        */}
                {/* ================================================================= */}
                {frame >= 34 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 130,
                      left: -20,
                      right: -20,
                      backgroundColor: bgCard,
                      borderRadius: 24,
                      border: `3px solid ${emeraldSuccess}`,
                      boxShadow: "0 20px 50px rgba(16, 185, 129, 0.35), 0 8px 20px rgba(0,0,0,0.08)",
                      padding: "20px 28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transform: `translateZ(60px) scale(${notif1Scale}) translateY(${notif1Y}px)`,
                      zIndex: 50,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 18,
                          backgroundColor: emeraldLight,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3.5">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 18, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                          STATUS REALTIME: SELESAI
                        </div>
                        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: textDark, letterSpacing: "0.02em" }}>
                          DIAMOND SUKSES MASUK (0.82 DETIK)!
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        backgroundColor: emeraldSuccess,
                        color: "#FFFFFF",
                        padding: "8px 18px",
                        borderRadius: 14,
                        fontFamily: "'Chakra Petch', sans-serif",
                        fontSize: 16,
                        fontWeight: 800,
                      }}
                    >
                      LIVE NOW
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* SCENE 2: REALTIME ORDER STATUS 3D TRACKER (Frames 110 - 425)          */}
          {/* ===================================================================== */}
          {frame >= 105 && frame < 435 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [105, 120, 410, 430],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: purpleLight,
                  border: `1.5px solid ${kyuPurple}`,
                  padding: "6px 20px",
                  borderRadius: 20,
                  marginBottom: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(93, 63, 211, 0.15)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: kyuPurple, letterSpacing: "0.08em" }}>
                  FITUR 1: NOTIFIKASI STATUS PESANAN LIVE
                </span>
              </div>

              <h1
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 68,
                  textAlign: "center",
                  lineHeight: 1.02,
                  marginBottom: 20,
                }}
              >
                PANTAU TRANSAKSIMU <span style={{ color: emeraldSuccess }}>DETIK PER DETIK</span>
              </h1>

              {/* 3D Master Glass Stage */}
              <div
                style={{
                  position: "relative",
                  width: 820,
                  height: 520,
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 36,
                  border: `2.5px solid ${kyuPurple}`,
                  boxShadow: "0 35px 80px rgba(93, 63, 211, 0.2), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "32px 36px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={80} haloWidth={240} borderRadius={36} />

                {/* Step 1 Card: QRIS Payment Verified */}
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 24,
                    border: "2px solid #E2E8F0",
                    padding: "20px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: `translateZ(30px) scale(${interpolate(b2Step1, [0, 1], [0.85, 1])})`,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: emeraldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: textMuted }}>
                        LANGKAH 1
                      </div>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 34, color: textDark, letterSpacing: "0.02em" }}>
                        PEMBAYARAN QRIS TERVERIFIKASI
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 18, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.04em" }}>
                    TERKONFIRMASI INSTAN
                  </div>
                </div>

                {/* Step 2 Card: Direct Moonton Inject */}
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 24,
                    border: `2.5px solid ${emeraldSuccess}`,
                    padding: "20px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: `translateZ(45px) scale(${interpolate(b2Step2, [0, 1], [0.85, 1])})`,
                    boxShadow: "0 15px 35px rgba(16, 185, 129, 0.2)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: emeraldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: emeraldSuccess }}>
                        LANGKAH 2 (OFFICIAL MOONTON API)
                      </div>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 34, color: textDark, letterSpacing: "0.02em" }}>
                        DIRECT INJECT MOONTON SUKSES
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: emeraldSuccess,
                      color: "#FFFFFF",
                      padding: "8px 20px",
                      borderRadius: 14,
                      fontFamily: "'Chakra Petch', sans-serif",
                      fontSize: 18,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                    }}
                  >
                    0.82 DETIK
                  </div>
                </div>

                {/* Floating Big Diamond Payoff Badge */}
                <div
                  style={{
                    backgroundColor: "#1E124A",
                    color: "#FFFFFF",
                    borderRadius: 26,
                    padding: "22px 32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: `translateZ(65px) scale(${interpolate(b2Diamond, [0, 1], [0.8, 1])})`,
                    boxShadow: "0 22px 50px rgba(30, 18, 74, 0.4)",
                    marginTop: 6,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <Img src={staticFile("diamond_3d_crystal.png")} style={{ width: 56, height: 56, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: "#00F0FF", letterSpacing: "0.08em" }}>
                        HASIL INJECT SERVER REALTIME
                      </div>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 38, letterSpacing: "0.02em" }}>
                        +220 DIAMOND SIAP DIGUNAKAN DI GAME!
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 800, color: "#10B981" }}>
                    100% ANTI-MINUS
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* SCENE 3: FLASH SALE & PROMO RADAR (Frames 425 - 675)                  */}
          {/* ===================================================================== */}
          {frame >= 420 && frame < 680 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [420, 435, 660, 680],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              {/* Alert Radar Badge */}
              <div
                style={{
                  backgroundColor: goldLight,
                  border: `1.5px solid ${solarGold}`,
                  padding: "6px 20px",
                  borderRadius: 20,
                  marginBottom: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(255, 176, 32, 0.2)",
                }}
              >
                <div style={{ transform: `rotate(${bellRing}deg)` }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={solarGold} stroke={solarGold} strokeWidth="1">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </div>
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: "#B45309", letterSpacing: "0.08em" }}>
                  FITUR 2: RADAR PROMO & FLASH SALE
                </span>
              </div>

              <h1
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 68,
                  textAlign: "center",
                  lineHeight: 1.02,
                  marginBottom: 20,
                }}
              >
                DAPATKAN DISKON <span style={{ color: solarGold }}>SEBELUM KUOTA LUDES!</span>
              </h1>

              {/* 3D Flash Sale Card with Bell and Official Product */}
              <div
                style={{
                  position: "relative",
                  width: 820,
                  height: 520,
                  backgroundColor: "rgba(255, 255, 255, 0.96)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 36,
                  border: `2.5px solid ${solarGold}`,
                  boxShadow: "0 35px 85px rgba(255, 176, 32, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "36px 40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(b3CardSpring, [0, 1], [0.85, 1])})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={36} />

                {/* Left Side: Product Offer & Prices */}
                <div style={{ maxWidth: 440, transform: "translateZ(30px)" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      backgroundColor: "#FEE2E2",
                      color: "#DC2626",
                      padding: "6px 16px",
                      borderRadius: 12,
                      fontFamily: "'Chakra Petch', sans-serif",
                      fontSize: 16,
                      fontWeight: 800,
                      marginBottom: 12,
                    }}
                  >
                    FLASH SALE HANYA HARI INI
                  </div>

                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 54, lineHeight: 1.05, color: textDark }}>
                    WEEKLY DIAMOND PASS MLBB
                  </div>

                  <div style={{ display: "flex", alignItems: "baseline", gap: 16, margin: "16px 0" }}>
                    <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 66, color: kyuPurple }}>
                      RP 25.000
                    </span>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 28, fontWeight: 700, color: textMuted, textDecoration: "line-through" }}>
                      Rp 35.000
                    </span>
                  </div>

                  <div
                    style={{
                      backgroundColor: goldLight,
                      border: `1.5px solid ${solarGold}`,
                      padding: "12px 20px",
                      borderRadius: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 800, color: "#92400E" }}>
                      Notifikasi prioritas dikirim ke HP sebelum kehabisan!
                    </span>
                  </div>
                </div>

                {/* Right Side: 3D Weekly Pass Image + Bouncing Discount Badge */}
                <div
                  style={{
                    position: "relative",
                    width: 280,
                    height: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(55px)",
                  }}
                >
                  <Img
                    src={staticFile("official_weekly_pass_clean.png")}
                    style={{
                      width: 260,
                      height: 260,
                      objectFit: "contain",
                      filter: "drop-shadow(0 20px 30px rgba(93, 63, 211, 0.3))",
                    }}
                  />

                  {/* Bouncing -70% Badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      backgroundColor: "#EF4444",
                      color: "#FFFFFF",
                      borderRadius: 18,
                      padding: "10px 18px",
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: 32,
                      boxShadow: "0 10px 25px rgba(239, 68, 68, 0.4)",
                      transform: `translateY(${discountBounce}px) rotate(8deg)`,
                    }}
                  >
                    HEMAT 70%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* SCENE 4: RESOLUTION & CTA (Frames 675 - 900)                          */}
          {/* ===================================================================== */}
          {frame >= 670 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [670, 685], [0, 1], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: emeraldLight,
                  border: `1.5px solid ${emeraldSuccess}`,
                  padding: "6px 20px",
                  borderRadius: 20,
                  marginBottom: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                </svg>
                <span style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: 14, fontWeight: 700, color: emeraldSuccess, letterSpacing: "0.08em" }}>
                  TOP UP TENANG • PROMO SELALU DAPAT
                </span>
              </div>

              <h1
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 70,
                  textAlign: "center",
                  lineHeight: 1.02,
                  marginBottom: 24,
                }}
              >
                AKTIFKAN NOTIFIKASIMU SEKARANG <br />
                <span style={{ color: kyuPurple }}>DI KYUSTORE.COM</span>
              </h1>

              {/* Master CTA Card */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: "rgba(255, 255, 255, 0.96)",
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${kyuPurple}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(b4Spring, [0, 1], [0.85, 1])})`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Kyu Store Brand Unit: Large Logo + Bold Readable Text */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    marginBottom: 26,
                    transform: "translateZ(35px)",
                  }}
                >
                  <Img
                    src={staticFile("kyu_logo.png")}
                    style={{
                      height: 110,
                      objectFit: "contain",
                      filter: "drop-shadow(0 12px 28px rgba(93, 63, 211, 0.28))",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontFamily: "'Bebas Neue', sans-serif",
                        fontSize: 54,
                        color: textDark,
                        lineHeight: 0.95,
                        letterSpacing: "0.02em",
                      }}
                    >
                      KYU STORE
                    </span>
                    <span
                      style={{
                        fontFamily: "'Chakra Petch', sans-serif",
                        fontSize: 18,
                        fontWeight: 700,
                        color: emeraldSuccess,
                        letterSpacing: "0.1em",
                      }}
                    >
                      DISTRIBUTOR RESMI MOONTON INDONESIA
                    </span>
                  </div>
                </div>

                {/* Big Glowing CTA Button */}
                <div
                  style={{
                    backgroundColor: emeraldSuccess,
                    color: "#FFFFFF",
                    padding: "22px 54px",
                    borderRadius: 26,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "0 18px 45px rgba(16, 185, 129, 0.38)",
                    transform: `translateZ(50px) scale(${ctaPulse})`,
                    marginBottom: 26,
                  }}
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, letterSpacing: "0.04em" }}>
                    AKTIFKAN NOTIFIKASI DI KYUSTORE.COM
                  </span>
                </div>

                {/* Trust Points */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 32,
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 20,
                    fontWeight: 800,
                    color: textMuted,
                    transform: "translateZ(30px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>100% Legal & Aman</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Proses 0.8 Detik Satset</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Cukup User ID & Zone</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 18 Atmospheric Floating Light Motes */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* FOOTER TICKER (Safe Zone Bottom 80px)                                     */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: bgCard,
          border: "2px solid #E2E8F0",
          borderRadius: 22,
          padding: "14px 30px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 800, color: textMuted }}>
          <span style={{ color: kyuPurple }}>METODE PEMBAYARAN RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Chakra Petch', sans-serif", fontSize: 16, fontWeight: 700, color: emeraldSuccess }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>AUTO INJECT ACTIVE 24/7</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: PINNED AT OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
